/**
 * @copyright Copyright 2022-2025 Epic Systems Corporation
 * @file Table header with sortable columns
 * @module Epic.AppOrchard.Core.Filter.SortedColumnHeader
 */

import { BackgroundProps, Box, Flex, IconButton, Spacer, Td, Tr } from "@chakra-ui/react";
import { IColumnDefinition, ISorting } from "ao/types";
import { FC } from "react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { AOTooltip, IAOTooltipStylingProps } from "../AOTooltip";

type IProps = {
	columns: IColumnDefinition[];
	sorting: ISorting;
	handleUpdateSortColumn: (columnKey: string) => void;
} & (
	| {
			stickyHeader: true;
			backgroundColor?: BackgroundProps["backgroundColor"];
	  }
	| { stickyHeader?: false; backgroundColor?: never }
) &
	(
		| {
				useAOTooltip: true;
				aoTooltipProps?: IAOTooltipStylingProps;
		  }
		| { useAOTooltip?: false; aoTooltipProps?: never }
	);

export const SortableColumnHeader: FC<IProps> = (props: IProps) => {
	const {
		columns,
		sorting,
		handleUpdateSortColumn,
		stickyHeader,
		backgroundColor,
		useAOTooltip,
		aoTooltipProps,
	} = props;

	return (
		<Tr {...(stickyHeader ? { position: "sticky", top: 0, background: backgroundColor ?? "white" } : {})}>
			{columns.map((column) => {
				const cellContent = (
					<Flex>
						<Box fontWeight={"bold"} display="flex" alignItems="center">
							{column.name}
						</Box>
						<Spacer></Spacer>
						{column.isSortable && (
							<IconButton
								size="xs"
								aria-label={"Sort on " + column.name}
								title={"Sort on " + column.name}
								icon={
									sorting.sortColumn !== column.key ? (
										<FaSort />
									) : sorting.sortDirection === 1 ? (
										<FaSortDown />
									) : (
										<FaSortUp />
									)
								}
								variant="ghost"
							></IconButton>
						)}
					</Flex>
				);
				return (
					<Td
						key={column.key}
						title={useAOTooltip ? undefined : column.tooltip}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
						onClick={() => {
							if (column.isSortable) handleUpdateSortColumn(column.key);
						}}
						cursor={column.isSortable ? "pointer" : undefined}
						_hover={{ bg: "gray.100" }}
						padding={useAOTooltip ? 0 : undefined}
					>
						{useAOTooltip ? (
							<AOTooltip
								key={`tooltip-for-column-header-${column.key}`}
								helptext={column.tooltip}
								label={column.tooltip}
								aria-label={column.tooltip}
								hasArrow
								boxProps={{
									display: "flex",
									color: "black",
									paddingX: "4",
									paddingY: "2",
									...aoTooltipProps?.boxProps,
								}}
								placement="bottom"
								fontSize="md"
								padding="2"
								marginY="1"
								marginX="2"
								boxShadow="dark-lg"
								borderRadius="6px"
								{...aoTooltipProps}
							>
								{/* Fontsize cascade workaround for AOTooltip QAN#7920269 and QAN#7917109 */}
								<Flex fontSize={aoTooltipProps?.fontSize || "sm"}>{cellContent}</Flex>
							</AOTooltip>
						) : (
							cellContent
						)}
					</Td>
				);
			})}
		</Tr>
	);
};
